import React from 'react';
import { Icon } from '@iconify/react';
import { Avatar, Chip } from '@heroui/react';
import { Sidebar, useSidebar } from '@heroui-pro/react';
import { BuildCommit } from '@/components/buildCommit';
import { ModeBadge } from '@/components/modeBadge';
import { useSignOut } from '@/router/useSignOut';
import { getDeviceBranch, getNameInitials, getUser } from '@/utils';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PATH } from '@/router/PATH';
import {
    isNavigationItemActive,
    SECURED_NAVIGATION_GROUPS,
    SECURED_NAVIGATION_ITEMS,
    type SecuredNavigationGroup,
    type SecuredNavigationItem,
} from '../navigation';
import { DashboardLanguageToggle } from './DashboardLanguageToggle';
import { DashboardThemeToggle } from './DashboardThemeToggle';

type DashboardSidebarProps = {
    pathname: string;
};

type SidebarContentsProps = {
    idPrefix?: string;
    pathname: string;
    showThemeToggle?: boolean;
};

type SidebarNavItemProps = {
    idPrefix: string;
    item: SecuredNavigationItem;
    pathname: string;
};

type SidebarNavGroupProps = {
    group: SecuredNavigationGroup;
    idPrefix: string;
    pathname: string;
};

export const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
    pathname,
}) => {
    return (
        <>
            <Sidebar data-testid="dashboard-sidebar" id="dashboard-sidebar">
                <SidebarContents pathname={pathname} />
            </Sidebar>
            <Sidebar.Mobile
                data-testid="dashboard-sidebar-mobile"
                id="dashboard-sidebar-mobile"
            >
                <SidebarContents
                    idPrefix="mobile-"
                    pathname={pathname}
                    showThemeToggle
                />
            </Sidebar.Mobile>
        </>
    );
};

const SidebarContents: React.FC<SidebarContentsProps> = ({
    idPrefix = '',
    pathname,
    showThemeToggle = false,
}) => {
    const { t } = useTranslation('components');
    // Sign-out revokes the refresh token on the server, so a shared device
    // can't reuse the session.
    const onLogout = useSignOut();

    // The signed-in branch admin + their enrolled branch drive the profile
    // block (falling back to the generic labels before sign-in).
    const user = getUser();
    const branch = getDeviceBranch();
    const profileName = user?.name ?? t('layout.profile.name');
    const profileSecondary = branch?.name ?? t('layout.profile.role');
    const profileInitials = getNameInitials(profileName);

    const defaultExpandedKeys = React.useMemo(
        () =>
            SECURED_NAVIGATION_GROUPS.filter((group) =>
                group.items.some((item) =>
                    isNavigationItemActive(item, pathname),
                ),
            ).map(
                (group) =>
                    `${idPrefix}dashboard-sidebar-group-item-${group.slug}`,
            ),
        [idPrefix, pathname],
    );

    return (
        <>
            <Sidebar.Header
                data-testid={`${idPrefix}dashboard-sidebar-header`}
                id={`${idPrefix}dashboard-sidebar-header`}
            >
                <div
                    className="flex items-center gap-3 px-1 py-1"
                    data-testid={`${idPrefix}dashboard-sidebar-profile`}
                    id={`${idPrefix}dashboard-sidebar-profile`}
                >
                    <Avatar
                        className="size-9"
                        data-testid={`${idPrefix}dashboard-sidebar-avatar`}
                        id={`${idPrefix}dashboard-sidebar-avatar`}
                    >
                        <Avatar.Fallback
                            data-testid={`${idPrefix}dashboard-sidebar-avatar-fallback`}
                            id={`${idPrefix}dashboard-sidebar-avatar-fallback`}
                        >
                            {profileInitials}
                        </Avatar.Fallback>
                    </Avatar>
                    <div
                        className="flex min-w-0 flex-col"
                        data-sidebar="label"
                        data-testid={`${idPrefix}dashboard-sidebar-profile-copy`}
                        id={`${idPrefix}dashboard-sidebar-profile-copy`}
                    >
                        <span
                            className="text-sm font-medium leading-tight text-foreground"
                            data-testid={`${idPrefix}dashboard-sidebar-profile-name`}
                            id={`${idPrefix}dashboard-sidebar-profile-name`}
                        >
                            {profileName}
                        </span>
                        <span
                            className="text-xs font-medium leading-tight text-muted"
                            data-testid={`${idPrefix}dashboard-sidebar-profile-role`}
                            id={`${idPrefix}dashboard-sidebar-profile-role`}
                        >
                            {profileSecondary}
                        </span>
                    </div>
                </div>
            </Sidebar.Header>
            <Sidebar.Content
                data-testid={`${idPrefix}dashboard-sidebar-content`}
                id={`${idPrefix}dashboard-sidebar-content`}
            >
                <Sidebar.Group
                    data-testid={`${idPrefix}dashboard-sidebar-group`}
                    id={`${idPrefix}dashboard-sidebar-group`}
                >
                    <Sidebar.Menu
                        aria-label={t('layout.aria.navigation')}
                        data-testid={`${idPrefix}dashboard-sidebar-menu`}
                        defaultExpandedKeys={defaultExpandedKeys}
                        id={`${idPrefix}dashboard-sidebar-menu`}
                    >
                        {SECURED_NAVIGATION_ITEMS.map((item) => (
                            <SidebarNavItem
                                idPrefix={idPrefix}
                                item={item}
                                key={`${idPrefix}${item.href}`}
                                pathname={pathname}
                            />
                        ))}
                        {SECURED_NAVIGATION_GROUPS.map((group) => (
                            <SidebarNavGroup
                                group={group}
                                idPrefix={idPrefix}
                                key={`${idPrefix}${group.slug}`}
                                pathname={pathname}
                            />
                        ))}
                    </Sidebar.Menu>
                </Sidebar.Group>
            </Sidebar.Content>
            <Sidebar.Footer
                data-testid={`${idPrefix}dashboard-sidebar-footer`}
                id={`${idPrefix}dashboard-sidebar-footer`}
            >
                {showThemeToggle ? (
                    <>
                        <div
                            className="flex items-center justify-between gap-3 px-1 py-1"
                            data-testid={`${idPrefix}dashboard-sidebar-language-row`}
                            id={`${idPrefix}dashboard-sidebar-language-row`}
                        >
                            <span
                                className="text-sm font-medium text-foreground"
                                data-testid={`${idPrefix}dashboard-sidebar-language-label`}
                                id={`${idPrefix}dashboard-sidebar-language-label`}
                            >
                                {t('layout.language.label')}
                            </span>
                            <DashboardLanguageToggle
                                className="gap-0"
                                id={`${idPrefix}dashboard-sidebar-language-toggle`}
                                itemIdPrefix={`${idPrefix}dashboard-sidebar`}
                            />
                        </div>
                        <div
                            className="flex items-center justify-between gap-3 px-1 py-1"
                            data-testid={`${idPrefix}dashboard-sidebar-theme-row`}
                            id={`${idPrefix}dashboard-sidebar-theme-row`}
                        >
                            <span
                                className="text-sm font-medium text-foreground"
                                data-testid={`${idPrefix}dashboard-sidebar-theme-label`}
                                id={`${idPrefix}dashboard-sidebar-theme-label`}
                            >
                                {t('layout.theme.label')}
                            </span>
                            <DashboardThemeToggle
                                className="gap-0"
                                id={`${idPrefix}dashboard-sidebar-theme-toggle`}
                                itemIdPrefix={`${idPrefix}dashboard-sidebar`}
                            />
                        </div>
                    </>
                ) : null}
                <Sidebar.Menu
                    aria-label={t('layout.aria.account')}
                    data-testid={`${idPrefix}dashboard-sidebar-footer-menu`}
                    id={`${idPrefix}dashboard-sidebar-footer-menu`}
                >
                    <Sidebar.MenuItem
                        data-testid={`${idPrefix}dashboard-sidebar-logout`}
                        id={`${idPrefix}dashboard-sidebar-logout`}
                        onAction={onLogout}
                        textValue={t('layout.actions.logOut')}
                    >
                        <Sidebar.MenuIcon
                            data-testid={`${idPrefix}dashboard-sidebar-logout-icon-wrap`}
                            id={`${idPrefix}dashboard-sidebar-logout-icon-wrap`}
                        >
                            <Icon
                                className="size-4"
                                data-testid={`${idPrefix}dashboard-sidebar-logout-icon`}
                                icon="solar:logout-3-linear"
                                id={`${idPrefix}dashboard-sidebar-logout-icon`}
                            />
                        </Sidebar.MenuIcon>
                        <Sidebar.MenuLabel
                            data-testid={`${idPrefix}dashboard-sidebar-logout-label`}
                            id={`${idPrefix}dashboard-sidebar-logout-label`}
                        >
                            {t('layout.actions.logOut')}
                        </Sidebar.MenuLabel>
                        <Sidebar.MenuChip
                            data-testid={`${idPrefix}dashboard-sidebar-logout-chip-wrap`}
                            id={`${idPrefix}dashboard-sidebar-logout-chip-wrap`}
                        >
                            <Chip
                                color="danger"
                                data-testid={`${idPrefix}dashboard-sidebar-logout-chip`}
                                id={`${idPrefix}dashboard-sidebar-logout-chip`}
                                size="sm"
                                variant="soft"
                            >
                                {t('layout.actions.exit')}
                            </Chip>
                        </Sidebar.MenuChip>
                    </Sidebar.MenuItem>
                </Sidebar.Menu>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-1">
                    <BuildCommit />
                    <ModeBadge />
                </div>
            </Sidebar.Footer>
        </>
    );
};

const SidebarNavGroup: React.FC<SidebarNavGroupProps> = ({
    group,
    idPrefix,
    pathname,
}) => {
    const { t } = useTranslation('components');
    const label = t(group.labelKey);
    const groupItemId = `${idPrefix}dashboard-sidebar-group-item-${group.slug}`;

    return (
        <Sidebar.MenuItem
            data-testid={groupItemId}
            id={groupItemId}
            textValue={label}
        >
            <Sidebar.MenuIcon
                data-testid={`${groupItemId}-icon-wrap`}
                id={`${groupItemId}-icon-wrap`}
            >
                <Icon
                    className="size-4"
                    data-testid={`${groupItemId}-icon`}
                    icon={group.icon}
                    id={`${groupItemId}-icon`}
                />
            </Sidebar.MenuIcon>
            <Sidebar.MenuLabel
                data-testid={`${groupItemId}-label`}
                id={`${groupItemId}-label`}
            >
                {label}
            </Sidebar.MenuLabel>
            <Sidebar.MenuTrigger
                data-testid={`${groupItemId}-trigger`}
                id={`${groupItemId}-trigger`}
            >
                <Sidebar.MenuIndicator
                    data-testid={`${groupItemId}-indicator`}
                    id={`${groupItemId}-indicator`}
                />
            </Sidebar.MenuTrigger>
            <Sidebar.Submenu>
                {group.items.map((item) => (
                    <SidebarNavItem
                        idPrefix={idPrefix}
                        item={item}
                        key={`${idPrefix}${item.href}`}
                        pathname={pathname}
                    />
                ))}
            </Sidebar.Submenu>
        </Sidebar.MenuItem>
    );
};

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
    idPrefix,
    item,
    pathname,
}) => {
    const { t } = useTranslation('components');
    const navigate = useNavigate();
    const { isMobile, setMobileOpen } = useSidebar();
    const isCurrent = (item.matchPaths ?? [item.href]).some(
        (matchPath) =>
            pathname === matchPath ||
            (matchPath !== PATH.HOME && pathname.startsWith(`${matchPath}/`)),
    );

    const label = t(item.labelKey);
    const slug = item.slug;
    const itemId = `${idPrefix}dashboard-sidebar-item-${slug}`;

    const onAction = React.useCallback(() => {
        navigate(item.href);
        if (isMobile) {
            setMobileOpen(false);
        }
    }, [isMobile, item.href, navigate, setMobileOpen]);

    const onLinkClick = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.stopPropagation();

            if (
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            ) {
                return;
            }

            event.preventDefault();
            onAction();
        },
        [onAction],
    );

    const onLinkAuxClick = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.stopPropagation();
        },
        [],
    );

    return (
        <Sidebar.MenuItem
            data-testid={itemId}
            id={itemId}
            isCurrent={isCurrent}
            onAction={onAction}
            textValue={label}
        >
            <a
                className="flex min-w-0 flex-1 items-center gap-3 text-inherit no-underline"
                data-testid={`${itemId}-link`}
                href={item.href}
                id={`${itemId}-link`}
                onAuxClick={onLinkAuxClick}
                onClick={onLinkClick}
            >
                <Sidebar.MenuIcon
                    data-testid={`${itemId}-icon-wrap`}
                    id={`${itemId}-icon-wrap`}
                >
                    <Icon
                        className="size-4"
                        data-testid={`${itemId}-icon`}
                        icon={item.icon}
                        id={`${itemId}-icon`}
                    />
                </Sidebar.MenuIcon>
                <Sidebar.MenuLabel
                    data-testid={`${itemId}-label`}
                    id={`${itemId}-label`}
                >
                    {label}
                </Sidebar.MenuLabel>
                {item.badge ? (
                    <Sidebar.MenuChip
                        data-testid={`${itemId}-chip-wrap`}
                        id={`${itemId}-chip-wrap`}
                    >
                        <Chip
                            color="success"
                            data-testid={`${itemId}-chip`}
                            id={`${itemId}-chip`}
                            size="sm"
                            variant="soft"
                        >
                            {t(item.badge)}
                        </Chip>
                    </Sidebar.MenuChip>
                ) : null}
            </a>
        </Sidebar.MenuItem>
    );
};
